<template>
  <ul :class="['ui-switch', { 'is-disabled': disabled }]">
    <li
      v-for="(option, index) in options"
      :key="`option-${index}`"
      class="ui-switch__option"
      :class="{ 'is-active': option.value === internalValue }"
    >
      <input
        :id="generateId(option)"
        v-model="internalValue"
        class="ui-switch__input"
        :disabled="disabled"
        :value="option.value"
        :name="name"
        type="radio"
      />
      <label class="ui-switch__label" :class="{ active: !disabled }" :for="generateId(option)">
        {{ option.label }}
      </label>
    </li>
  </ul>
</template>

<script lang="ts" setup>
import { PropType, ref, toRefs, watch } from 'vue';

type SwitchValue = string | number | boolean;

interface SwitchOption {
  label: string;
  value: SwitchValue;
}

const props = defineProps({
  options: {
    type: Array as PropType<SwitchOption[]>,
    required: true,
  },
  modelValue: {
    type: [String, Number, Boolean] as PropType<SwitchValue>,
    default: null,
  },
  disabled: Boolean,
  name: {
    type: String,
    default: 'switch',
  },
});

const emit = defineEmits(['update:modelValue']);

const { modelValue, options } = toRefs(props);
const internalValue = ref(modelValue.value || options.value[0].value);

watch(modelValue, (val) => {
  internalValue.value = val;
});

watch(internalValue, (val) => {
  emit('update:modelValue', val);
});

const generateId = (option: SwitchOption): string => {
  return ['switch', props.name, option.label]
    .filter((s) => s)
    .join('-')
    .replace(/\s+/g, '-');
};
</script>

<style lang="scss" scoped>
.ui-switch {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--dark-pink);
  border: var(--input-text-border-width) solid var(--dark-pink);
  border-radius: rem(30);

  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &__input {
    @include visually-hidden;
  }

  &__option {
    --option-margin: 3px;

    width: 100%;
    margin: var(--option-margin);
    border-radius: rem(100);
    cursor: pointer;
    text-align: center;

    &.is-active {
      background: var(--primary);
      color: var(--dark-pink);
    }

    &:first-child {
      margin: var(--option-margin) 0 var(--option-margin) var(--option-margin);
    }

    &:last-child {
      margin: var(--option-margin) var(--option-margin) var(--option-margin) 0;
    }
  }

  &__label {
    display: block;
    cursor: pointer;
    white-space: nowrap;
    padding: rem(12);
    font-weight: 500;
    @include mobile {
      padding: rem(8);
      font-size: rem(14);
    }
    @include tablet {
      font-size: rem(15);
    }
    @include desktop {
      font-size: rem(16);
    }
  }
}
</style>
